import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useFormikContext } from 'formik';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { getPageFieldNames, TaskItemPage } from './paged-task-items';

interface FormPageNavProps {
  pages: TaskItemPage[];
  currentPage: number;
  submitting?: boolean;
  submitLabel?: string;
  onBack: () => void;
  onNext: () => void;
  onGoToPage: (page: number) => void;
}

/**
 * Next validates only the fields on the current page. The schema stays whole
 * form so the final submit still checks everything, and a failed submit sends
 * the user back to the first page still holding an error.
 */
export const FormPageNav = ({
  pages,
  currentPage,
  submitting,
  submitLabel = 'Submit',
  onBack,
  onNext,
  onGoToPage,
}: FormPageNavProps) => {
  const { validateForm, setTouched, touched, errors, submitCount } = useFormikContext<Record<string, any>>();
  const { theme } = useCampfireTheme();
  const isLastPage = currentPage >= pages.length - 1;
  const isFirstPage = currentPage === 0;
  const handledSubmitCount = useRef(0);

  const fieldNamesByPage = useMemo(() => pages.map(getPageFieldNames), [pages]);

  const erroredFieldsOnPage = (pageIndex: number, formErrors: Record<string, unknown>) =>
    (fieldNamesByPage[pageIndex] ?? []).filter((name) => Boolean(formErrors[name]));

  const markTouched = (fieldNames: string[]) => {
    const nextTouched = { ...touched } as Record<string, unknown>;
    fieldNames.forEach((name) => {
      nextTouched[name] = true;
    });
    setTouched(nextTouched, false);
  };

  useEffect(() => {
    if (submitCount === 0 || submitCount === handledSubmitCount.current) return;

    const formErrors = errors as Record<string, unknown>;
    const erroredPage = fieldNamesByPage.findIndex((fieldNames) =>
      fieldNames.some((name) => Boolean(formErrors[name]))
    );
    if (erroredPage === -1) return;

    handledSubmitCount.current = submitCount;
    if (erroredPage !== currentPage) onGoToPage(erroredPage);
  }, [submitCount, errors, fieldNamesByPage, currentPage, onGoToPage]);

  const handleNext = async () => {
    const errs = (await validateForm()) as Record<string, unknown>;
    const pageErrors = erroredFieldsOnPage(currentPage, errs);

    if (pageErrors.length > 0) {
      markTouched(pageErrors);
      return;
    }

    onNext();
  };

  if (pages.length < 2) return null;

  return (
    <Box display='flex' alignItems='center' justifyContent='space-between' paddingTop={3} paddingRight={1}>
      <Box flex={1} display='flex' justifyContent='flex-start' alignItems='center'>
        {!isFirstPage && (
          <TabletButton
            key='page-back'
            type='button'
            variant='outlined'
            size='large'
            onClick={onBack}
            style={{ borderRadius: 4, minWidth: 100 }}
          >
            Back
          </TabletButton>
        )}
      </Box>

      <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' paddingX={2}>
        <Box display='flex' alignItems='center' style={{ gap: 4 }}>
          {pages.map((page, index) => (
            <Box
              key={page.key}
              width={18}
              height={4}
              borderRadius={2}
              bgcolor={index <= currentPage ? theme.palette.primary.main : theme.color.grey.neutralBrand200}
            />
          ))}
        </Box>
        <Typography variant='caption' style={{ color: theme.color.grey.neutral300, marginTop: 10 }}>
          {`Page ${currentPage + 1} of ${pages.length}`}
        </Typography>
      </Box>

      <Box flex={1} display='flex' justifyContent='flex-end' alignItems='center'>
        {isLastPage ? (
          <TabletButton
            key='page-submit'
            color='primary'
            variant='contained'
            type='submit'
            size='large'
            style={{ borderRadius: 4, minWidth: 120, opacity: submitting ? 0.5 : 1 }}
          >
            {submitting ? 'Submitting...' : submitLabel}
          </TabletButton>
        ) : (
          <TabletButton
            key='page-next'
            type='button'
            color='primary'
            variant='contained'
            size='large'
            onClick={handleNext}
            style={{ borderRadius: 4, minWidth: 120 }}
          >
            Next
          </TabletButton>
        )}
      </Box>
    </Box>
  );
};
